import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, Check, Crown, Shield, Zap } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import YocoPayment from '../components/YocoPayment';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function SubscriptionPage({ user }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [subStatus, setSubStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedPlan, setSelectedPlan] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [plansRes, statusRes] = await Promise.all([
        fetch(`${API}/subscription/plans`),
        user ? fetch(`${API}/subscription/status`, {
          headers: { 'Authorization': `Bearer ${token}` }
        }) : Promise.resolve(null)
      ]);

      if (plansRes.ok) {
        const plansData = await plansRes.json();
        setPlans(plansData);
      }
      if (statusRes?.ok) {
        setSubStatus(await statusRes.json());
      }
    } catch (error) {
      toast.error('Failed to load subscription plans');
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentSuccess = () => {
    toast.success('Subscription activated! You now have full access.');
    setTimeout(() => navigate('/dashboard'), 2000);
  };

  const isActive = subStatus?.subscription_status === 'active';
  const monthlyPlan = plans.find(p => p.interval === 'monthly');
  const annualPlan = plans.find(p => p.interval === 'annual');
  const monthlyCostOfAnnual = annualPlan ? (annualPlan.price / 12).toFixed(0) : 0;

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4EFE6] flex items-center justify-center">
        <div className="text-[#0B0B0B] text-xl">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#161616]" data-testid="subscription-page">
      <nav className="bg-[#161616] border-b border-white/5 px-6 py-4">
        <div className="container mx-auto flex justify-between items-center">
          <div className="text-2xl font-bold text-white" style={{ fontFamily: 'Clash Display' }}>
            Move<span className="text-[#C6A97C]">More</span>
          </div>
          <Button
            data-testid="back-btn"
            onClick={() => navigate(user ? '/dashboard' : '/')}
            variant="ghost"
            className="text-white hover:bg-white/10 rounded-full"
          >
            <ArrowLeft className="w-4 h-4 mr-2" /> Back
          </Button>
        </div>
      </nav>

      <div className="container mx-auto px-4 sm:px-6 py-8 sm:py-16">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center gap-2 bg-[#D9C7A3]/10 text-[#C6A97C] px-4 py-2 rounded-full text-sm font-medium mb-6">
            <Crown className="w-4 h-4" /> ALL-ACCESS SUBSCRIPTION
          </div>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4" style={{ fontFamily: 'Clash Display' }}>
            Unlock <span className="text-[#C6A97C]">Everything</span>
          </h1>
          <p className="text-base sm:text-lg text-gray-400 max-w-2xl mx-auto px-2">
            Get unlimited access to every course, exercise, and future content. One subscription, total freedom.
          </p>
        </motion.div>

        {isActive && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="max-w-xl mx-auto mb-12"
          >
            <Card className="bg-[#D9C7A3]/10 border border-[#C6A97C]/30 rounded-2xl p-8 text-center" data-testid="active-subscription-card">
              <Shield className="w-12 h-12 text-[#C6A97C] mx-auto mb-4" />
              <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Clash Display' }}>
                You're Subscribed!
              </h2>
              <p className="text-gray-400 mb-2">
                Plan: <span className="text-[#C6A97C] font-medium">{subStatus.subscription_plan || 'All-Access'}</span>
              </p>
              {subStatus.subscription_end_date && (
                <p className="text-gray-500 text-sm">
                  Access until: {new Date(subStatus.subscription_end_date).toLocaleDateString('en-ZA', { year: 'numeric', month: 'long', day: 'numeric' })}
                </p>
              )}
              <Button
                data-testid="go-to-courses-btn"
                onClick={() => navigate('/courses')}
                className="mt-6 bg-[#D9C7A3] text-[#0B0B0B] hover:bg-[#C6A97C] rounded-full px-8"
              >
                Browse All Courses
              </Button>
            </Card>
          </motion.div>
        )}

        {!isActive && (
          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto mb-16">
            {monthlyPlan && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
              >
                <Card
                  className={`rounded-2xl p-5 sm:p-8 h-full flex flex-col transition-all cursor-pointer ${
                    selectedPlan?.id === monthlyPlan.id
                      ? 'bg-white/10 border-2 border-[#C6A97C]'
                      : 'bg-white/5 border border-white/10 hover:border-white/30'
                  }`}
                  onClick={() => setSelectedPlan(monthlyPlan)}
                  data-testid="monthly-plan-card"
                >
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Clash Display' }}>
                      {monthlyPlan.name}
                    </h3>
                    <p className="text-gray-400 mb-6 text-sm">{monthlyPlan.description}</p>
                    <div className="mb-6">
                      <span className="text-4xl font-bold text-white">R{monthlyPlan.price.toFixed(0)}</span>
                      <span className="text-gray-400 text-lg">/month</span>
                    </div>
                    <ul className="space-y-3 mb-8">
                      {monthlyPlan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2 text-gray-300">
                          <Check className="w-5 h-5 text-[#C6A97C] flex-shrink-0 mt-0.5" />
                          <span className="text-sm">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {user ? (
                    selectedPlan?.id === monthlyPlan.id ? (
                      <YocoPayment
                        packageType="subscription"
                        planId={monthlyPlan.id}
                        courseName={monthlyPlan.name}
                        onSuccess={handlePaymentSuccess}
                      />
                    ) : (
                      <Button
                        className="w-full border border-white/20 hover:bg-white/5 text-white rounded-full py-4 sm:py-6 text-base sm:text-lg"
                        data-testid="select-monthly-btn"
                      >
                        Select Monthly
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={() => navigate('/?login=true')}
                      className="w-full border border-white/20 hover:bg-white/5 text-white rounded-full py-4 sm:py-6 text-base sm:text-lg"
                    >
                      Login to Subscribe
                    </Button>
                  )}
                </Card>
              </motion.div>
            )}

            {annualPlan && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.2 }}
              >
                <Card
                  className={`rounded-2xl p-5 sm:p-8 h-full flex flex-col relative transition-all cursor-pointer ${
                    selectedPlan?.id === annualPlan.id
                      ? 'bg-[#D9C7A3] text-[#0B0B0B] border-2 border-[#C6A97C]'
                      : 'bg-[#D9C7A3] text-[#0B0B0B]'
                  }`}
                  onClick={() => setSelectedPlan(annualPlan)}
                  data-testid="annual-plan-card"
                >
                  <div className="absolute top-4 right-4 bg-[#0B0B0B] text-[#C6A97C] px-3 py-1 rounded-full text-xs font-bold">
                    BEST VALUE
                  </div>
                  <div className="flex-1">
                    <h3 className="text-2xl font-bold mb-2" style={{ fontFamily: 'Clash Display' }}>
                      {annualPlan.name}
                    </h3>
                    <p className="text-[#0B0B0B]/70 mb-6 text-sm">{annualPlan.description}</p>
                    <div className="mb-2">
                      <span className="text-4xl font-bold">R{annualPlan.price.toFixed(0)}</span>
                      <span className="text-[#0B0B0B]/70 text-lg">/year</span>
                    </div>
                    <p className="text-sm text-[#0B0B0B]/60 mb-6">
                      That's just R{monthlyCostOfAnnual}/month
                    </p>
                    <ul className="space-y-3 mb-8">
                      {annualPlan.features.map((feature, i) => (
                        <li key={i} className="flex items-start gap-2">
                          <Check className="w-5 h-5 text-[#0B0B0B] flex-shrink-0 mt-0.5" />
                          <span className="text-sm font-medium">{feature}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  {user ? (
                    selectedPlan?.id === annualPlan.id ? (
                      <YocoPayment
                        packageType="subscription"
                        planId={annualPlan.id}
                        courseName={annualPlan.name}
                        onSuccess={handlePaymentSuccess}
                      />
                    ) : (
                      <Button
                        className="w-full bg-[#0B0B0B] text-[#C6A97C] hover:bg-[#161616] rounded-full py-4 sm:py-6 text-base sm:text-lg font-bold"
                        data-testid="select-annual-btn"
                      >
                        Select Annual
                      </Button>
                    )
                  ) : (
                    <Button
                      onClick={() => navigate('/?login=true')}
                      className="w-full bg-[#0B0B0B] text-[#C6A97C] hover:bg-[#161616] rounded-full py-4 sm:py-6 text-base sm:text-lg font-bold"
                    >
                      Login to Subscribe
                    </Button>
                  )}
                </Card>
              </motion.div>
            )}
          </div>
        )}

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="max-w-3xl mx-auto"
        >
          <div className="text-center mb-10">
            <h2 className="text-2xl font-bold text-white mb-2" style={{ fontFamily: 'Clash Display' }}>
              Why Subscribe?
            </h2>
            <p className="text-gray-400">Everything you need for your recovery journey</p>
          </div>
          <div className="grid sm:grid-cols-3 gap-6">
            {[
              { icon: Crown, title: 'All Courses', desc: 'Instant access to every program on the platform' },
              { icon: Zap, title: 'New Content', desc: 'First access to new exercises and programs as they launch' },
              { icon: Shield, title: 'Best Value', desc: 'Save significantly compared to buying courses individually' }
            ].map((item, i) => (
              <Card key={i} className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center">
                <item.icon className="w-10 h-10 text-[#C6A97C] mx-auto mb-3" />
                <h3 className="text-white font-bold mb-1" style={{ fontFamily: 'Clash Display' }}>{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.desc}</p>
              </Card>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
